import { Badge, Heading, LuIcon, radiusCva, Text, useLayoutContext, useProvider } from "@overmap-ai/blocks"
import { type Project } from "@overmap-ai/common"
import { selectAccessibleProjects, selectCurrentUser } from "@overmap-ai/core"
import { Link, useNavigate, type ValidateLinkOptions } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo, useCallback } from "react"

import { UserSettingsDropdownMenu } from "../../components/DropdownMenus/UserSettingsDropdownMenu"
import { ProjectSelect } from "../../components/DropdownSelects/ProjectSelect"
import { UserIcon } from "../../components/Icons/UserIcon"
import { navigationLink, navigationLinkIcon } from "../../cva"
import { SLIDE_OUT_IDS } from "../../layouts"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectActiveOrganization, selectActiveProject } from "../../store/selectors"

interface RouteDetails {
	label: string
	icon: (typeof modelIconMapping)[keyof typeof modelIconMapping]
	to: ValidateLinkOptions["to"]
	activeOptions?: ValidateLinkOptions["activeOptions"]
}

const organizationRoutes = [
	{
		label: "Projects",
		icon: modelIconMapping.project,
		to: "/$organizationId/projects",
		activeOptions: {
			exact: true,
		},
	},
	{
		label: "Forms",
		icon: modelIconMapping.form,
		to: "/$organizationId/forms",
	},
	{
		label: "Documents",
		icon: modelIconMapping.document,
		to: "/$organizationId/documents",
	},
	{
		label: "Reports",
		icon: modelIconMapping.report,
		to: "/$organizationId/reports",
	},
] satisfies RouteDetails[]

const projectRoutes = [
	{
		label: "Map",
		icon: modelIconMapping.map,
		to: "/$organizationId/$projectId",
		activeOptions: { exact: true, includeSearch: false },
	},
	{
		label: "Timeline",
		icon: modelIconMapping.history,
		to: "/$organizationId/$projectId/timeline",
		activeOptions: { exact: true, includeSearch: false },
	},
	{ label: "Assets", icon: modelIconMapping.assetType, to: "/$organizationId/$projectId/asset-types" },
	{
		label: "Initiatives",
		icon: modelIconMapping.procedureInitiave,
		to: "/$organizationId/$projectId/initiatives",
	},
	{
		label: "Procedures",
		icon: modelIconMapping.checklist,
		to: "/$organizationId/$projectId/procedure-types",
	},
	{ label: "Issues", icon: modelIconMapping.issue, to: "/$organizationId/$projectId/issue-types" },
	{
		label: "Forms",
		icon: modelIconMapping.form,
		to: "/$organizationId/$projectId/forms",
	},
	{ label: "Images", icon: modelIconMapping.camera, to: "/$organizationId/$projectId/images" },
	{
		label: "Documents",
		icon: modelIconMapping.document,
		to: "/$organizationId/$projectId/documents",
	},
] satisfies RouteDetails[]

export const AppNavigation = memo(() => {
	const activeOrganization = useAppSelector(selectActiveOrganization)!
	const activeProject = useAppSelector(selectActiveProject)
	const user = useAppSelector(selectCurrentUser)!
	const projects = useShallowEqaulSelector(selectAccessibleProjects(user.id))
	const navigate = useNavigate()
	const { setOpen, small } = useLayoutContext()
	const { radius } = useProvider()

	const handleRouteClick = useCallback(() => {
		if (small) setOpen(SLIDE_OUT_IDS.LEFT, false)
	}, [setOpen, small])

	const handleProjectSelect = useCallback(
		(project: Project | undefined) => {
			if (!project) return

			void navigate({
				from: `/$organizationId`,
				to: `/$organizationId/$projectId`,
				params: {
					projectId: project.uuid,
				},
				search: {},
			})
		},
		[navigate],
	)

	return (
		<nav className="flex h-full w-full flex-col p-4">
			<div className="flex h-max w-full items-center gap-2 overflow-hidden pb-4">
				<Badge accentColor="base" variant="soft" className="shrink-1! min-w-0 grow !justify-start">
					<Text className="truncate">{activeOrganization.name}</Text>
				</Badge>

				<UserSettingsDropdownMenu size="sm">
					<UserIcon userId={user?.id} size="md" />
				</UserSettingsDropdownMenu>
			</div>

			<div className="flex flex-col gap-4">
				<section className="flex flex-col gap-2">
					<Heading accentColor="base" className="px-2">
						Organization
					</Heading>

					<div className="flex flex-col gap-0.5">
						{organizationRoutes.map(({ to, icon, label, activeOptions }) => (
							<Link
								className={cx(navigationLink(), radiusCva({ radius, maxLarge: false }))}
								activeOptions={activeOptions}
								from="/$organizationId"
								to={to}
								key={to}
								onClick={handleRouteClick}
								tabIndex={0}
								draggable={false}
							>
								<LuIcon icon={icon} className={navigationLinkIcon()} />
								<span className="truncate">{label}</span>
							</Link>
						))}
					</div>
				</section>

				<section className="flex flex-col gap-2">
					<ProjectSelect
						required={false}
						value={activeProject ?? undefined}
						onValueChange={handleProjectSelect}
						projects={projects}
						size="sm"
					>
						<Heading
							className="shrink-1! flex min-w-0 grow items-center !justify-between px-2"
							size="md"
							accentColor="base"
						>
							<span className="truncate">{activeProject?.name ?? "Select project"}</span>
							<LuIcon icon={modelIconMapping.chevronDown} />
						</Heading>
					</ProjectSelect>

					{activeProject && (
						<>
							<div className="flex flex-col gap-0.5">
								{projectRoutes.map(({ to, icon, label, activeOptions }) => (
									<Link
										className={cx(navigationLink(), radiusCva({ radius, maxLarge: false }))}
										from="/$organizationId"
										activeOptions={activeOptions}
										params={(prev) => ({ ...prev, projectId: activeProject.uuid })}
										to={to}
										key={to}
										onClick={handleRouteClick}
										tabIndex={0}
										draggable={false}
									>
										<LuIcon icon={icon} className={navigationLinkIcon()} />
										<span className="truncate">{label}</span>
									</Link>
								))}
							</div>
						</>
					)}
				</section>
			</div>
		</nav>
	)
})

AppNavigation.displayName = "AppNavigation"
